import { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { getFoundationBridgeHealth } from "@/lib/api";

const POLL_MS = 5 * 60_000;
const ROLES = ["admin", "accounting", "office"];
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : null);

// SVC-FIX-4b — Finance/Admin see a plain-English banner while the Foundation bridge is down, and a toast when it is back.
export default function BridgeStatusBanner() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [dismissedAt, setDismissedAt] = useState(null);
  const allowed = !!user && ROLES.includes((user.role || "").toLowerCase());

  useEffect(() => {
    if (!allowed) return;
    let prevOk = null;
    const tick = async () => {
      try {
        const { data } = await getFoundationBridgeHealth();
        setState(data);
        if (prevOk === false && data.ok) toast.success("Foundation bridge is back — queued pushes and cost pulls are running now");
        prevOk = data.ok;
      } catch { /* leave the last known state */ }
    };
    tick();
    const t = setInterval(tick, POLL_MS);
    return () => clearInterval(t);
  }, [allowed]);

  if (!allowed || !state || state.ok || !state.configured) return null;
  if (dismissedAt && state.down_since === dismissedAt) return null;
  const since = fmtTime(state.down_since);
  const lastGood = fmtTime(state.last_ok_at);
  return (
    <div className="flex items-start gap-2 px-4 py-2 bg-rose-50 border-b border-rose-200 text-rose-900 text-sm" data-testid="bridge-status-banner">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-rose-600" />
      <div className="flex-1 min-w-0">
        <span className="font-semibold" data-testid="bridge-status-banner-text">{state.status_text}</span>
        {state.hint && <span className="text-rose-800"> — {state.hint}</span>}
        <span className="text-rose-700 text-xs ml-2">
          {since ? `down since ${since}` : ""}{lastGood ? ` · last good ${lastGood}` : ""} · Foundation pushes and cost pulls are queued and run automatically when it is back.
        </span>
        {state.detail && (
          <details className="text-xs text-rose-700 mt-0.5"><summary className="cursor-pointer" data-testid="bridge-status-banner-details">details</summary>
            <pre className="whitespace-pre-wrap text-[10px]">{typeof state.detail === "string" ? state.detail : JSON.stringify(state.detail, null, 2)}</pre></details>
        )}
      </div>
      <button className="p-1 text-rose-500 hover:text-rose-800" onClick={() => setDismissedAt(state.down_since)} aria-label="Dismiss" data-testid="bridge-status-banner-dismiss"><X className="w-4 h-4" /></button>
    </div>
  );
}
